import { ArrowDown, ArrowRight } from "lucide-react";
import { Navbar, Footer } from "./LandingPage";
import NetworkBackground from "./NetworkBackground";
import TiltCard from "./TiltCard";
import { ProductVideoProvider, ProductVideoPreview, ProductVideoStory } from "./ProductVideoExperience";
import { productVideos, productVideoById } from "./productVideoData";

const contactLinkClass =
  "inline-flex items-center gap-3 rounded-sm py-2 text-sm font-semibold text-[#76b900] hover:text-[#8ce500] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#76b900]";

// Preserve the mouse-following green spotlight without moving the card's click targets.
const ProductCard = ({ children }) => (
  <TiltCard
    max={0}
    lift={0}
    scale={1}
    spotlight
    outerClassName="min-w-0"
    className="group liquid-glass relative h-full overflow-hidden rounded-2xl transition-colors duration-300"
  >
    {children}
  </TiltCard>
);

// Textes de présentation provisoires : exemples à valider avec CARTHY avant leur version définitive.
const rackProducts = [
  {
    id: "rack-console",
    number: "02",
    format: "Écran et clavier intégrés",
    name: "Serveur rack avec écran et clavier",
    description:
      "Un ordinateur au format rack avec écran intégré et tiroir clavier coulissant, pour accéder directement à la machine.",
    image: "/products/rack-console-carthy.webp",
    alt: "Serveur CARTHY noir avec écran intégré et tiroir clavier ouvert, éclairé sur fond noir",
  },
  {
    id: "rack-8-baies",
    number: "03",
    format: "Huit baies en façade",
    name: "Serveur rack 8 baies CARTHY",
    description:
      "Un serveur au format rack doté de huit tiroirs de disques en façade, pour réunir calcul et stockage dans un même châssis.",
    image: "/products/serveur-8-baies-carthy.webp",
    alt: "Serveur rack CARTHY avec huit tiroirs de disques et badge en façade, éclairé sur fond noir",
  },
];

const ProductsPage = () => (
  <ProductVideoProvider>
  <div className="products-page relative min-h-screen overflow-x-clip bg-black text-white antialiased">
    <NetworkBackground />
    <div className="relative z-10">
      <a
        href="#products-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-6 focus:top-6 focus:z-[60] focus:rounded-lg focus:bg-[#76b900] focus:px-5 focus:py-3 focus:font-semibold focus:text-black"
      >
        Aller au contenu
      </a>
      <Navbar homePath="/" />

      <main id="products-content" tabIndex={-1}>
        <section aria-labelledby="products-title" className="relative pb-16 pt-36 md:pb-24 lg:pt-40">
          <div className="mx-auto max-w-7xl px-6">
            <div className="products-section-heading mb-10 flex flex-col gap-6 md:mb-12 md:flex-row md:items-end md:justify-between md:gap-12">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#76b900]">Produits CARTHY</p>
                <h1
                  id="products-title"
                  className="mt-5 font-display text-5xl font-bold leading-[1.04] tracking-tighter sm:text-6xl xl:text-7xl"
                >
                  Le matériel<br />
                  <span className="text-[#76b900]">CARTHY.</span>
                </h1>
              </div>
              <p className="max-w-md text-lg font-light leading-relaxed text-white/65">
                Du format tour au serveur rack, découvrez nos équipements et échangeons sur la configuration adaptée à votre environnement.
              </p>
            </div>

            <ProductCard>
              <article aria-labelledby="tour-title" className="grid min-w-0 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
                <div className="order-2 p-6 sm:p-8 lg:order-1 lg:py-12 lg:pl-10 lg:pr-4">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#76b900]">01 / Format tour</p>
                  <h2 id="tour-title" className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Tour GPU CARTHY</h2>
                  <p className="mt-5 max-w-md leading-relaxed text-white/65">
                    Une station au format tour qui réunit plusieurs cartes graphiques et les composants de calcul dans un châssis sur roulettes.
                  </p>
                  <a href="/#contact" className={`${contactLinkClass} mt-6`} aria-label="Parler de la tour GPU CARTHY">
                    Parler de ce produit <ArrowRight size={18} aria-hidden="true" />
                  </a>
                </div>

                <div className="product-media relative order-1 flex min-w-0 items-center justify-center overflow-hidden border-b border-white/10 p-4 sm:p-6 lg:order-2 lg:min-h-[410px] lg:border-0 lg:p-4">
                  <ProductVideoPreview
                    product={productVideoById["tour-gpu"]}
                    alt="Tour GPU CARTHY noire sur roulettes, avec son emblème vert et un éclairage de studio"
                    priority
                  />
                </div>
              </article>
            </ProductCard>

            <a
              href="#formats-rack"
              className="mt-8 inline-flex w-fit items-center gap-3 rounded-full border border-white/10 bg-black/30 px-5 py-3 text-sm text-white/70 transition-colors hover:border-[#76b900]/50 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#76b900]"
            >
              Découvrir les formats rack <ArrowDown size={16} aria-hidden="true" />
            </a>
          </div>
        </section>

        <section id="formats-rack" aria-labelledby="rack-title" className="products-section scroll-mt-24 py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <div className="products-section-heading mb-10 flex flex-col gap-5 md:mb-14 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#76b900]">Formats rack</p>
                <h2 id="rack-title" className="mt-4 font-display text-3xl font-semibold tracking-tighter md:text-5xl">
                  Deux formats.<br />Deux usages.
                </h2>
              </div>
              <p className="max-w-sm leading-relaxed text-white/60">
                Un accès direct par écran et clavier, ou huit baies de stockage en façade : choisissez le format qui répond à votre projet.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {rackProducts.map((product) => (
                <ProductCard key={product.id}>
                  <article aria-labelledby={`${product.id}-title`} className="flex h-full min-w-0 flex-col">
                    <div className="product-media relative aspect-[16/10] shrink-0">
                      <ProductVideoPreview product={productVideoById[product.id]} alt={product.alt} />
                    </div>
                    <div className="flex flex-1 flex-col border-t border-white/10 p-6 sm:p-8">
                      <p className="font-mono text-xs uppercase leading-relaxed tracking-[0.15em] text-[#76b900]">
                        {product.number} / {product.format}
                      </p>
                      <h3 id={`${product.id}-title`} className="mt-4 font-display text-2xl font-semibold leading-tight tracking-tight">
                        {product.name}
                      </h3>
                      <p className="mb-6 mt-4 leading-relaxed text-white/65">{product.description}</p>
                      <a href="/#contact" className={`${contactLinkClass} mt-auto self-start`} aria-label={`Parler du ${product.name}`}>
                        Parler de ce produit <ArrowRight size={18} aria-hidden="true" />
                      </a>
                    </div>
                  </article>
                </ProductCard>
              ))}
            </div>
          </div>
        </section>

        {productVideos.map((product) => <ProductVideoStory key={product.id} product={product} />)}

        <section aria-labelledby="product-contact-title" className="products-section px-6 py-20 md:py-24">
          <div className="products-contact-panel mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 lg:flex-row lg:items-center lg:gap-16">
            <div className="max-w-2xl">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#76b900]">Parlons de votre projet</p>
              <h2 id="product-contact-title" className="mt-4 font-display text-3xl font-semibold tracking-tighter md:text-4xl">
                Trouvons votre configuration.
              </h2>
              <p className="mt-5 leading-relaxed text-white/65">
                Présentez-nous vos usages et votre environnement. Ensemble, précisons les équipements et les composants dont vous avez besoin.
              </p>
            </div>
            <a
              href="/#contact"
              className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#76b900] px-4 py-4 text-center text-sm font-semibold text-black shadow-[0_0_24px_rgba(118,185,0,0.25)] transition-colors hover:bg-[#8ce500] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#76b900] sm:w-auto sm:gap-3 sm:px-6"
            >
              Planifier une rencontre <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer homePath="/" />
    </div>
  </div>
  </ProductVideoProvider>
);

export default ProductsPage;
